/**
 * ABM Parentescos — Fase 8
 *
 * Catálogo simple (solo descripción) usado en el módulo de Grupo familiar
 * del afiliado. Hard delete con restricción por FK.
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import { Add, Edit, Delete } from '@mui/icons-material';

import {
  useParentescos,
  useCreateParentesco,
  useUpdateParentesco,
  useDeleteParentesco,
  type Parentesco,
} from '../../../hooks/useCatalogos';

export default function ParentescosList() {
  const { data: parentescos = [], isLoading, isError, error } = useParentescos();
  const create = useCreateParentesco();
  const update = useUpdateParentesco();
  const del = useDeleteParentesco();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Parentesco | null>(null);
  const [descripcion, setDescripcion] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDescripcion('');
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (p: Parentesco) => {
    setEditing(p);
    setDescripcion(p.descripcion);
    setFormError(null);
    setDialogOpen(true);
  };

  const handleDelete = async (p: Parentesco) => {
    if (!confirm(`¿Eliminar el parentesco "${p.descripcion}"?`)) return;
    try {
      await del.mutateAsync(p.id);
    } catch (e) {
      alert(`No se pudo eliminar: ${(e as Error).message}`);
    }
  };

  const handleSubmit = async () => {
    const d = descripcion.trim();
    if (d.length < 2 || d.length > 100) {
      setFormError('La descripción debe tener entre 2 y 100 caracteres');
      return;
    }
    try {
      if (editing) await update.mutateAsync({ id: editing.id, descripcion: d });
      else await create.mutateAsync(d);
      setDialogOpen(false);
    } catch {
      // se muestra abajo
    }
  };

  const apiError = (editing ? update.error : create.error) as Error | null;
  const submitting = (editing ? update.isPending : create.isPending) || del.isPending;

  const columns = useMemo<GridColDef<Parentesco>[]>(
    () => [
      { field: 'descripcion', headerName: 'Descripción', flex: 1, minWidth: 250 },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<Parentesco>) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => openEdit(p.row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton size="small" color="error" onClick={() => handleDelete(p.row)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Parentescos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Catálogo usado en el módulo de Grupo familiar del afiliado.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Nuevo parentesco
        </Button>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={parentescos}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
          localeText={{ noRowsLabel: 'No hay parentescos cargados' }}
        />
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Editar parentesco' : 'Nuevo parentesco'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {apiError && <Alert severity="error">{apiError.message}</Alert>}
            {formError && <Alert severity="warning">{formError}</Alert>}
            <TextField
              autoFocus
              required
              fullWidth
              label="Descripción"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              inputProps={{ maxLength: 100 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {editing ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
